import { Modal } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  Log,
  rideArchiveConfirmAtom,
  RideDeleteActionsResultEnum,
  useSystemParams,
} from "@ryde/shared";

import {
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  MuiButton,
} from "../controls";
import { ConfirmRideContent } from "./confirms.styled";

const MODAL_WIDTH = "530px";

export const ConfirmRideArchiveContainer: FC = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const [confirmData, setRideArchiveData] = useRecoilState(
    rideArchiveConfirmAtom,
  );

  const { markBookingAsNewOnRideDeletedOrCanceled } = useSystemParams();

  const isModalOpen = useMemo(() => {
    return !!confirmData;
  }, [confirmData]);

  const okClickHandler = useCallback(
    (result: RideDeleteActionsResultEnum) => async () => {
      if (!confirmData) {
        Log.warnAndToast(t("errors.INVALID_DELETE_HANDLER_DATA"));
        return;
      }

      const { resolveFn } = confirmData;

      if (result === RideDeleteActionsResultEnum.CancelBooking) {
        const confirmationResult = await confirmDialogResult({
          title: t("ride.CAUTION_TITLE"),
          message: t("ride.THIS_ACTION_WILL_CANCEL_BOOKINGS"),
          okButtonTitle: t("ride.cancelBookings"),
          cancelButtonTitle: t("ride.CANCEL_THIS_ACTION"),
        });

        if (confirmationResult === ConfirmResult.OK) {
          resolveFn(result);
        } else {
          resolveFn(RideDeleteActionsResultEnum.NoAction);
        }
      } else {
        resolveFn(result);
      }

      Log.debug("Ride archive handler with result:", result, confirmData);
      setRideArchiveData(null);
    },
    [confirmData, confirmDialogResult, setRideArchiveData, t],
  );

  const ModalHeader = useMemo(() => {
    return (
      <ModalHeaderStyled>
        <h1>
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.ARE_YOU_SURE`
              : `general.REMOVE_RIDES_TITLE`,
          )}
        </h1>
        <SVG_MUI.XMarkSVG
          onClick={okClickHandler(RideDeleteActionsResultEnum.NoAction)}
        />
      </ModalHeaderStyled>
    );
  }, [markBookingAsNewOnRideDeletedOrCanceled, okClickHandler, t]);

  const ModalContent = useMemo(() => {
    return (
      <ModalContentStyled>
        <ConfirmRideContent>
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.ARCHIVE_ITEMS_MESSAGE`
              : `general.REMOVE_CANCEL_RIDES_CONTENT`,
          )}
        </ConfirmRideContent>
      </ModalContentStyled>
    );
  }, [markBookingAsNewOnRideDeletedOrCanceled, t]);

  const ModalFooter = useMemo(() => {
    return (
      <ModalFooterStyled>
        <MuiButton
          muiStyle={"primary"}
          muiVariant={"outlined"}
          onClick={okClickHandler(RideDeleteActionsResultEnum.NoAction)}
          e2eData="cancel-modal-button"
        >
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.CANCEL`
              : `general.CLOSE`,
          )}
        </MuiButton>
        {!markBookingAsNewOnRideDeletedOrCanceled && (
          <MuiButton
            muiVariant={"outlined"}
            muiStyle={"warning"}
            onClick={okClickHandler(RideDeleteActionsResultEnum.CancelBooking)}
            e2eData="remove-completely-booking-button"
          >
            {t(`ride.cancelBookings`)}
          </MuiButton>
        )}

        <MuiButton
          onClick={okClickHandler(RideDeleteActionsResultEnum.NewBooking)}
          e2eData="ok-booking-button"
        >
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.OK`
              : `general.REBOOK`,
          )}
        </MuiButton>
      </ModalFooterStyled>
    );
  }, [markBookingAsNewOnRideDeletedOrCanceled, okClickHandler, t]);

  return (
    <>
      {isModalOpen && (
        <Modal open={isModalOpen || false} closeAfterTransition={true}>
          <ModalContainerStyled width={MODAL_WIDTH}>
            {ModalHeader}
            {ModalContent}
            {ModalFooter}
          </ModalContainerStyled>
        </Modal>
      )}
    </>
  );
};
